import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Navbar from '../components/Navbar';
import UploadZone from '../components/UploadZone';
import ResultsPanel from '../components/ResultsPanel';
import FAQ from '../components/FAQ';
import { parseInstagramFile, compareFollows, ComparisonResults } from '../utils/parser';
import { useSEO } from '../hooks/useSEO';
import { KOFI_URL, GUMROAD_URL, ADSENSE_SLOT, ADSENSE_CLIENT } from '../config';

// ─── Types ──────────────────────────────────────────────────────────────────
interface FileState {
  file: File | null;
  usernames: string[];
  error?: string;
  loading: boolean;
}

const initialFileState: FileState = {
  file: null,
  usernames: [],
  loading: false,
};

// ─── Affiliate links ─────────────────────────────────────────────────────────
const AFFILIATE_LINKS = [
  { name: 'Later',   desc: 'Schedule & grow your Instagram', url: 'https://later.com',    badge: null   },
  { name: 'Kicksta', desc: 'Organic follower growth tool',   url: 'https://kicksta.co?ref=yzzkmwq',  badge: 'Most popular' },
  { name: 'Planoly', desc: 'Visual Instagram planner',       url: 'https://planoly.com', badge: null              },
];

// ─── Sub-components ──────────────────────────────────────────────────────────
function AdSenseUnit({ consented }: { consented: boolean }) {
  if (!consented || ADSENSE_SLOT === 'YOUR_AD_SLOT_ID') return null;
  return (
    <div className="mt-8 overflow-hidden rounded-xl">
      <ins
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={ADSENSE_SLOT}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
}

function AffiliateSection() {
  return (
    <div className="mt-8">
      <p className="text-white/30 text-xs text-center uppercase tracking-widest mb-4">
        Want more followers? Try these tools
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {AFFILIATE_LINKS.map((link) => (
          <a
            key={link.name}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className="glass-card p-4 flex flex-col gap-1 hover:border-white/20 transition-all group"
          >
            <div className="flex items-center justify-between">
              <span className="text-white font-semibold text-sm">{link.name}</span>
              {link.badge && (
                <span className="text-xs ig-gradient-bg text-white px-2 py-0.5 rounded-full">
                  {link.badge}
                </span>
              )}
            </div>
            <p className="text-white/40 text-xs">{link.desc}</p>
            <p className="text-white/25 text-xs mt-1 group-hover:text-white/50 transition-colors">
              Try free →
            </p>
          </a>
        ))}
      </div>
      <p className="text-white/15 text-xs text-center mt-2">
        Affiliate links — we may earn a commission at no cost to you
      </p>
    </div>
  );
}

function GumroadProBanner() {
  return (
    <div className="mt-6 rounded-xl p-px ig-gradient-bg">
      <div className="rounded-xl bg-[#0D0D0D] px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <p className="text-white font-bold text-sm">✨ Love Purgr?</p>
          <p className="text-white/50 text-xs mt-0.5">
            Support the project — get a thank-you shoutout + early access to new features.
          </p>
        </div>
        <a
          href={GUMROAD_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-shrink-0 btn-gradient text-white text-xs font-bold px-5 py-2 rounded-full whitespace-nowrap"
        >
          Support ($1.99)
        </a>
      </div>
    </div>
  );
}

// ─── Home page ────────────────────────────────────────────────────────────────
interface HomeProps {
  consent: boolean;
  onPrivacyClick: () => void;
}

const Home: React.FC<HomeProps> = ({ consent, onPrivacyClick }) => {
  useSEO(
    'Purgr — See Who Doesn\'t Follow You Back on Instagram',
    'Find out who\'s not following you back on Instagram in seconds. 100% private — your files never leave your device. No login required.'
  );

  const [following, setFollowing] = useState<FileState>(initialFileState);
  const [followers, setFollowers] = useState<FileState>(initialFileState);
  const [results, setResults] = useState<ComparisonResults | null>(null);
  const [comparing, setComparing] = useState(false);

  const handleFollowingFile = useCallback(async (file: File) => {
    setFollowing({ file, usernames: [], loading: true });
    setResults(null);
    const result = await parseInstagramFile(file);
    setFollowing({ file, usernames: result.usernames, error: result.error, loading: false });
  }, []);

  const handleFollowersFile = useCallback(async (file: File) => {
    setFollowers({ file, usernames: [], loading: true });
    setResults(null);
    const result = await parseInstagramFile(file);
    setFollowers({ file, usernames: result.usernames, error: result.error, loading: false });
  }, []);

  const handleCompare = () => {
    if (!following.usernames.length || !followers.usernames.length) return;
    setComparing(true);
    setTimeout(() => {
      const r = compareFollows(following.usernames, followers.usernames);
      setResults(r);
      setComparing(false);
      setTimeout(() => {
        document.getElementById('results')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }, 400);
  };

  const canCompare =
    following.usernames.length > 0 &&
    followers.usernames.length > 0 &&
    !following.loading &&
    !followers.loading;

  const bothLoaded =
    following.file && followers.file && !following.error && !followers.error;

  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(ellipse at top, #1a0a1e 0%, #0D0D0D 60%)' }}>
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 pb-20">
        <Header />

        {/* Trust badge strip */}
        <div className="flex flex-wrap justify-center gap-2 mb-6 -mt-2">
          {[
            { icon: '🔒', text: 'No login required' },
            { icon: '🖥️', text: 'Runs in your browser' },
            { icon: '👁️', text: 'We never see your files' },
            { icon: '✅', text: '100% free' },
          ].map(({ icon, text }) => (
            <span key={text} className="glass-card px-3 py-1.5 text-xs text-white/70 flex items-center gap-1.5">
              <span>{icon}</span>
              <span>{text}</span>
            </span>
          ))}
        </div>

        {/* How it works */}
        <div className="glass-card p-6 mb-4">
          <h2 className="text-white/50 text-xs font-semibold uppercase tracking-widest mb-5 text-center">
            How it works
          </h2>
          <div className="grid grid-cols-3 gap-4 text-center">
            {[
              { step: '1', icon: '📱', text: 'Download your data from Instagram Settings → "Your activity" → "Download your information"' },
              { step: '2', icon: '📁', text: 'Find following.json and followers_1.json (or .html) in your downloaded zip' },
              { step: '3', icon: '🔍', text: 'Upload both files below and hit Compare — done!' },
            ].map(({ step, icon, text }) => (
              <div key={step} className="flex flex-col items-center gap-2">
                <div className="w-8 h-8 rounded-full ig-gradient-bg flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {step}
                </div>
                <div className="text-2xl">{icon}</div>
                <p className="text-white/50 text-xs leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-white/10 text-center">
            <p className="text-white/55 text-xs">
              🔒 <strong className="text-white/80">Your files never leave your device.</strong>{' '}
              Everything is processed locally — we never see your data.{' '}
              <button
                onClick={onPrivacyClick}
                className="text-white/40 hover:text-white/60 underline transition-colors"
              >
                How this works
              </button>
            </p>
          </div>
          <p className="text-center mt-3">
            <Link
              to="/how-to-download-instagram-data"
              className="text-white/35 hover:text-white/60 text-xs underline transition-colors"
            >
              Full step-by-step download guide →
            </Link>
          </p>
        </div>

        {/* Safety callout */}
        <div className="flex items-center gap-3 rounded-xl border border-green-500/20 bg-green-500/5 px-4 py-3 mb-6">
          <span className="text-green-400 text-lg flex-shrink-0">🔒</span>
          <p className="text-white/60 text-xs leading-relaxed">
            <span className="text-green-400 font-semibold">Safe to use.</span>{' '}
            Purgr never asks for your Instagram password. Your files are processed entirely in this browser window — nothing is uploaded to any server.
          </p>
        </div>

        {/* Upload section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <p className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2 ml-1">
              Step 1 — Following
            </p>
            <UploadZone
              label="Upload Following"
              sublabel="People YOU follow"
              icon="📤"
              fileName={following.file?.name}
              onFile={handleFollowingFile}
              error={following.error}
            />
            {following.usernames.length > 0 && (
              <p className="text-green-400/70 text-xs mt-2 ml-1">
                ✓ Found {following.usernames.length.toLocaleString()} {following.usernames.length === 1 ? 'account' : 'accounts'} in file
              </p>
            )}
            {following.loading && (
              <p className="text-white/40 text-xs mt-2 ml-1 animate-pulse">Reading file...</p>
            )}
          </div>

          <div>
            <p className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2 ml-1">
              Step 2 — Followers
            </p>
            <UploadZone
              label="Upload Followers"
              sublabel="People who follow YOU"
              icon="📥"
              fileName={followers.file?.name}
              onFile={handleFollowersFile}
              error={followers.error}
            />
            {followers.usernames.length > 0 && (
              <p className="text-green-400/70 text-xs mt-2 ml-1">
                ✓ Found {followers.usernames.length.toLocaleString()} {followers.usernames.length === 1 ? 'account' : 'accounts'} in file
              </p>
            )}
            {followers.loading && (
              <p className="text-white/40 text-xs mt-2 ml-1 animate-pulse">Reading file...</p>
            )}
          </div>
        </div>

        {/* Compare button */}
        <div className="flex justify-center mb-10">
          <button
            onClick={handleCompare}
            disabled={!canCompare || comparing}
            className="btn-gradient text-white font-bold py-4 px-12 rounded-full text-lg tracking-wide disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {comparing ? (
              <span className="flex items-center gap-2">
                <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Comparing...
              </span>
            ) : (
              '🔍 Compare Now'
            )}
          </button>
        </div>

        {bothLoaded && !canCompare && !following.loading && !followers.loading && (
          <p className="text-center text-red-400/70 text-sm -mt-6 mb-8">
            Fix the errors above before comparing.
          </p>
        )}

        {/* Results */}
        {results && (
          <div id="results">
            <h2 className="text-center text-white/50 text-xs font-semibold uppercase tracking-widest mb-4">
              Your Results
            </h2>
            <ResultsPanel results={results} />
            <GumroadProBanner />
            <AdSenseUnit consented={consent} />
            <AffiliateSection />
          </div>
        )}

        {/* FAQ */}
        <FAQ />

        {/* Footer */}
        <footer className="mt-16 text-center text-white/25 text-xs space-y-3">
          <p>Your data never leaves your browser. No accounts, no tracking, no uploads.</p>
          <div className="flex justify-center gap-3 flex-wrap">
            <a
              href={KOFI_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-white/50 hover:text-white/80 transition-colors border border-white/10 hover:border-white/25 rounded-full px-4 py-1.5"
            >
              ☕ Buy me a coffee
            </a>
            <a
              href={GUMROAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-white/50 hover:text-white/80 transition-colors border border-white/10 hover:border-white/25 rounded-full px-4 py-1.5"
            >
              ✨ Support ($1.99)
            </a>
            <Link
              to="/privacy"
              className="inline-flex items-center gap-1.5 text-white/50 hover:text-white/80 transition-colors border border-white/10 hover:border-white/25 rounded-full px-4 py-1.5"
            >
              Privacy Policy
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center gap-1.5 text-white/50 hover:text-white/80 transition-colors border border-white/10 hover:border-white/25 rounded-full px-4 py-1.5"
            >
              About
            </Link>
          </div>
          <div className="flex justify-center gap-3 flex-wrap text-white/20">
            <Link to="/terms" className="hover:text-white/50 transition-colors">Terms</Link>
            <span>·</span>
            <Link to="/contact" className="hover:text-white/50 transition-colors">Contact</Link>
            <span>·</span>
            <Link to="/how-to-download-instagram-data" className="hover:text-white/50 transition-colors">Download Guide</Link>
            <span>·</span>
            <Link to="/how-to-see-who-doesnt-follow-you-back" className="hover:text-white/50 transition-colors">See Non-Followers</Link>
            <span>·</span>
            <Link to="/how-to-clean-up-your-following-list" className="hover:text-white/50 transition-colors">Clean Up Following</Link>
            <span>·</span>
            <Link to="/best-free-instagram-unfollow-tracker" className="hover:text-white/50 transition-colors">Best Unfollow Tracker</Link>
            <span>·</span>
            <Link to="/what-is-a-good-follower-following-ratio" className="hover:text-white/50 transition-colors">Follower Ratio Guide</Link>
            <span>·</span>
            <Link to="/instagram-account-audit" className="hover:text-white/50 transition-colors">Instagram Audit</Link>
          </div>
          <p>© {new Date().getFullYear()} Purgr. Made with ♥</p>
        </footer>
      </div>
    </div>
  );
};

export default Home;
